import type {
  TaxConfigurationDetailRowDto,
  TaxConfigurationGroupDetailResponse,
  TaxReferenceDataSetDto,
} from './taxConfigurationApiTypes';
import { TAX_CONFIGURATION_DATA_CATEGORIES as DATA_CATEGORY } from './taxConfigurationDataCategories';

export interface TaxScaleItem {
  id: string;
  scaleCode: string;
  displayName: string;
  description: string;
  effectiveFrom: string;
  effectiveTo: string;
  active: string;
  status: string;
  source: string;
  importRun: string;
}

export interface TaxScaleBracketRow {
  id: string;
  scaleCode: string;
  earningsFrom: string;
  earningsTo: string;
  coefficientA: string;
  coefficientB: string;
  rate: string;
  isStepped: string;
  hasRate: boolean;
}

export interface TaxConfigurationFact {
  label: string;
  value: string;
}

export interface TaxConfigurationSectionRow extends Record<string, string> {
  id: string;
}

export interface TaxConfigurationSection {
  key: string;
  title: string;
  categoryLabel: string;
  rowCount: number;
  status: string;
  facts: TaxConfigurationFact[];
  rows: TaxConfigurationSectionRow[];
  columns: { field: string; header: string }[];
  displayMode: 'table' | 'fields';
  emptyMessage: string;
}

export const formatDateValue = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
};

export const formatEffectiveFrom = (value?: string) => (value ? formatDateValue(value) : 'Not returned');

export const formatEffectiveTo = (value?: string) => (value ? formatDateValue(value) : 'Open ended');

export const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Not returned';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateValue(value);
    return value;
  }
  return JSON.stringify(value);
};

const formatPercent = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : undefined;
  if (numeric === undefined || !Number.isFinite(numeric)) return formatValue(value);
  return new Intl.NumberFormat('en-AU', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'percent',
  }).format(numeric);
};

const valueFrom = (values: Record<string, unknown>, key: string) => {
  const value = values[key];
  return value === null || value === undefined || value === '' ? undefined : value;
};

const stringFrom = (values: Record<string, unknown>, key: string, fallback = 'Not returned') => {
  const value = valueFrom(values, key);
  return value === undefined ? fallback : String(value);
};

const numberFrom = (values: Record<string, unknown>, key: string) => {
  const value = valueFrom(values, key);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return undefined;
};

const formatMoney = (value: unknown) => {
  const numeric = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : undefined;
  if (numeric === undefined || !Number.isFinite(numeric)) return formatValue(value);
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(numeric);
};

export const sourceFromDataSet = (dataSet?: TaxReferenceDataSetDto) => (
  dataSet ? [dataSet.metadata?.source, dataSet.metadata?.sourceFilename].filter(Boolean).join(' / ') || 'Not returned' : 'Not returned'
);

const dataSetForScale = (dataSets: TaxReferenceDataSetDto[]) => (
  dataSets.find((dataSet) => dataSet.dataCategory === DATA_CATEGORY.TaxScaleCatalogue)
);

const toTaxScaleItem = (row: TaxConfigurationDetailRowDto, dataSet?: TaxReferenceDataSetDto): TaxScaleItem | null => {
  const values = row.values ?? {};
  const scaleCode = stringFrom(values, 'scaleCode', '');
  if (!scaleCode) return null;
  return {
    id: row.id,
    scaleCode,
    displayName: stringFrom(values, 'displayName', 'Display name not returned'),
    description: stringFrom(values, 'description', 'Description not returned'),
    effectiveFrom: formatEffectiveFrom(row.effectiveFrom),
    effectiveTo: formatEffectiveTo(row.effectiveTo),
    active: formatValue(valueFrom(values, 'isActive')),
    status: dataSet?.status || 'Not returned',
    source: sourceFromDataSet(dataSet),
    importRun: dataSet?.metadata?.importRunId ?? 'Not returned',
  };
};

const compareBracketRows = (left: TaxConfigurationDetailRowDto, right: TaxConfigurationDetailRowDto) => {
  const leftValues = left.values ?? {};
  const rightValues = right.values ?? {};
  const leftOrder = numberFrom(leftValues, 'bracketOrder');
  const rightOrder = numberFrom(rightValues, 'bracketOrder');
  if (leftOrder !== undefined || rightOrder !== undefined) {
    const difference = (leftOrder ?? Number.MAX_SAFE_INTEGER) - (rightOrder ?? Number.MAX_SAFE_INTEGER);
    if (difference !== 0) return difference;
  }
  const leftFrom = numberFrom(leftValues, 'lowerBound');
  const rightFrom = numberFrom(rightValues, 'lowerBound');
  if (leftFrom !== undefined || rightFrom !== undefined) {
    const difference = (leftFrom ?? Number.MAX_SAFE_INTEGER) - (rightFrom ?? Number.MAX_SAFE_INTEGER);
    if (difference !== 0) return difference;
  }
  const leftTo = numberFrom(leftValues, 'upperBound');
  const rightTo = numberFrom(rightValues, 'upperBound');
  if (leftTo !== undefined || rightTo !== undefined) {
    const difference = (leftTo ?? Number.MAX_SAFE_INTEGER) - (rightTo ?? Number.MAX_SAFE_INTEGER);
    if (difference !== 0) return difference;
  }
  return left.id.localeCompare(right.id);
};

const toBracketRow = (row: TaxConfigurationDetailRowDto): TaxScaleBracketRow => {
  const values = row.values ?? {};
  const rate = valueFrom(values, 'rate');
  return {
    id: row.id,
    scaleCode: stringFrom(values, 'scaleCode', ''),
    earningsFrom: formatMoney(valueFrom(values, 'lowerBound')),
    earningsTo: formatMoney(valueFrom(values, 'upperBound')),
    coefficientA: formatValue(valueFrom(values, 'coefficientA')),
    coefficientB: formatValue(valueFrom(values, 'coefficientB')),
    rate: formatValue(rate),
    isStepped: formatValue(valueFrom(values, 'isStepped')),
    hasRate: rate !== undefined,
  };
};

export const buildTaxScaleModel = (
  groupDetails: TaxConfigurationGroupDetailResponse | null,
) => {
  const rows = groupDetails?.rows ?? [];
  const dataSet = dataSetForScale(groupDetails?.dataSets ?? []);
  const scales = rows
    .filter((row) => row.dataCategory === DATA_CATEGORY.TaxScaleCatalogue)
    .map((row) => toTaxScaleItem(row, dataSet))
    .filter((scale): scale is TaxScaleItem => Boolean(scale));
  const bracketRows = rows
    .filter((row) => row.dataCategory === DATA_CATEGORY.PaygBracket || row.dataCategory === DATA_CATEGORY.WorkingHolidayMaker)
    .slice()
    .sort(compareBracketRows);
  const bracketsByScaleCode = new Map<string, TaxScaleBracketRow[]>();
  for (const row of bracketRows) {
    const bracket = toBracketRow(row);
    if (!bracket.scaleCode) continue;
    const existing = bracketsByScaleCode.get(bracket.scaleCode) ?? [];
    existing.push(bracket);
    bracketsByScaleCode.set(bracket.scaleCode, existing);
  }
  return { scales, bracketsByScaleCode };
};

type Formatter = 'date' | 'money' | 'percent' | 'value';

interface SectionFieldConfig {
  key: string;
  label: string;
  formatter?: Formatter;
}

interface SectionConfig {
  key: string;
  title: string;
  categoryLabel: string;
  categories: string[];
  fields: SectionFieldConfig[];
  displayMode?: 'table' | 'fields';
  emptyMessage: string;
  selectorSubtitle?: string;
}

const formatFieldValue = (field: SectionFieldConfig, value: unknown) => {
  if (field.formatter === 'date') return value ? formatDateValue(String(value)) : 'Not returned';
  if (field.formatter === 'money') return formatMoney(value);
  if (field.formatter === 'percent') return formatPercent(value);
  return formatValue(value);
};

const dataSetForCategories = (dataSets: TaxReferenceDataSetDto[], categories: string[]) =>
  dataSets.find((dataSet) => categories.includes(dataSet.dataCategory));

const factsFromDataSet = (dataSet?: TaxReferenceDataSetDto): TaxConfigurationFact[] => [
  { label: 'Source', value: sourceFromDataSet(dataSet) },
  { label: 'Import run', value: dataSet?.metadata?.importRunId ?? 'Not returned' },
];

const buildSection = (
  config: SectionConfig,
  groupDetails: TaxConfigurationGroupDetailResponse | null,
): TaxConfigurationSection => {
  const rows = (groupDetails?.rows ?? []).filter((row) => config.categories.includes(row.dataCategory));
  const dataSet = dataSetForCategories(groupDetails?.dataSets ?? [], config.categories);
  const columns = [
    ...config.fields.map((field) => ({ field: field.key, header: field.label })),
    { field: 'effectiveFrom', header: 'Effective from' },
    { field: 'effectiveTo', header: 'Effective to' },
  ];
  return {
    key: config.key,
    title: config.title,
    categoryLabel: config.selectorSubtitle ?? config.categories[0],
    rowCount: rows.length,
    status: dataSet?.status || 'Not returned',
    facts: factsFromDataSet(dataSet),
    rows: rows.map((row) => {
      const values = row.values ?? {};
      return {
        id: row.id,
        ...Object.fromEntries(config.fields.map((field) => [field.key, formatFieldValue(field, valueFrom(values, field.key))])),
        effectiveFrom: formatEffectiveFrom(row.effectiveFrom),
        effectiveTo: formatEffectiveTo(row.effectiveTo),
      };
    }),
    columns,
    displayMode: config.displayMode ?? 'table',
    emptyMessage: config.emptyMessage,
  };
};

const MEDICARE_STSL_SECTIONS: SectionConfig[] = [
  {
    key: 'medicare-levy-adjustment',
    title: 'Medicare Levy adjustment',
    categoryLabel: 'Medicare Levy adjustment',
    categories: [DATA_CATEGORY.MedicareLevyAdjustment],
    fields: [
      { key: 'parameterCode', label: 'Parameter Code' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', formatter: 'money' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
      { key: 'familyStatus', label: 'Family Status' },
    ],
    selectorSubtitle: 'MedicareLevyAdjustment',
    emptyMessage: 'No Medicare Levy adjustment rows returned for this selection.',
  },
  {
    key: 'stsl-rates',
    title: 'STSL rates',
    categoryLabel: 'STSL rates',
    categories: [DATA_CATEGORY.Stsl],
    fields: [
      { key: 'incomeFrom', label: 'Income from', formatter: 'money' },
      { key: 'incomeTo', label: 'Income to', formatter: 'money' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'Stsl',
    emptyMessage: 'No STSL rows returned for this selection.',
  },
];

const OFFSET_SECTIONS: SectionConfig[] = [
  {
    key: 'sapto',
    title: 'SAPTO',
    categoryLabel: 'SAPTO',
    categories: [DATA_CATEGORY.Sapto],
    fields: [
      { key: 'saptoCode', label: 'Sapto Code' },
      { key: 'threshold', label: 'Threshold', formatter: 'money' },
      { key: 'offsetAmount', label: 'Offset Amount', formatter: 'money' },
      { key: 'taperRate', label: 'Taper Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'Sapto',
    emptyMessage: 'No SAPTO rows returned for this selection.',
  },
  {
    key: 'tax-offset-reduction',
    title: 'Tax offset reduction',
    categoryLabel: 'Tax offset reduction',
    categories: [DATA_CATEGORY.TaxOffsetReduction],
    fields: [
      { key: 'offsetCode', label: 'Offset Code' },
      { key: 'threshold', label: 'Threshold', formatter: 'money' },
      { key: 'reductionRate', label: 'Reduction Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'TaxOffsetReduction',
    emptyMessage: 'No tax offset reduction rows returned for this selection.',
  },
];

const TERMINATION_SECTIONS: SectionConfig[] = [
  {
    key: 'termination-parameters',
    title: 'Termination parameters',
    categoryLabel: 'Termination parameters',
    categories: [DATA_CATEGORY.Termination, DATA_CATEGORY.DelayedTermination],
    fields: [
      { key: 'parameterType', label: 'Parameter Type' },
      { key: 'parameterCode', label: 'Parameter Code' },
      { key: 'amount', label: 'Amount', formatter: 'money' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
      { key: 'notes', label: 'Notes' },
    ],
    selectorSubtitle: 'Termination',
    emptyMessage: 'No termination parameter rows returned for this selection.',
  },
  {
    key: 'death-benefit-etp',
    title: 'Death Benefit ETP',
    categoryLabel: 'Death Benefit ETP',
    categories: [DATA_CATEGORY.DeathBenefitEtp],
    fields: [
      { key: 'etpType', label: 'Etp Type' },
      { key: 'benefitType', label: 'Benefit Type' },
      { key: 'dependantStatus', label: 'Dependant Status' },
      { key: 'preservationAgeStatus', label: 'Preservation Age Status' },
      { key: 'taxTreatment', label: 'Tax Treatment' },
      { key: 'capType', label: 'Cap Type' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'DeathBenefitEtp',
    emptyMessage: 'No Death Benefit ETP rows returned for this selection.',
  },
  {
    key: 'schedule-7-unused-leave',
    title: 'Schedule 7 unused leave',
    categoryLabel: 'Schedule 7 unused leave',
    categories: [DATA_CATEGORY.Schedule7UnusedLeave],
    fields: [
      { key: 'leaveType', label: 'Leave Type' },
      { key: 'accrualPeriod', label: 'Accrual Period' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'Schedule7UnusedLeave',
    emptyMessage: 'No Schedule 7 unused leave rows returned for this selection.',
  },
  {
    key: 'schedule-11-etp',
    title: 'Schedule 11 ETP',
    categoryLabel: 'Schedule 11 ETP',
    categories: [DATA_CATEGORY.Schedule11Etp],
    fields: [
      { key: 'paymentCode', label: 'Payment Code' },
      { key: 'taxTreatment', label: 'Tax Treatment' },
      { key: 'rate', label: 'Rate', formatter: 'percent' },
    ],
    selectorSubtitle: 'Schedule11Etp',
    emptyMessage: 'No Schedule 11 ETP rows returned for this selection.',
  },
];

const AGE_RULE_SECTIONS: SectionConfig[] = [
  {
    key: 'age-rules',
    title: 'Age rule',
    categoryLabel: 'Age rule',
    categories: [DATA_CATEGORY.AgeRule],
    fields: [
      { key: 'ruleType', label: 'Rule Type' },
      { key: 'birthDateFrom', label: 'Birth Date From', formatter: 'date' },
      { key: 'birthDateTo', label: 'Birth Date To', formatter: 'date' },
      { key: 'ageYears', label: 'Age Years' },
    ],
    selectorSubtitle: 'AgeRule',
    displayMode: 'fields',
    emptyMessage: 'No age rule rows returned for this selection.',
  },
];

const ADJUSTMENT_SECTIONS: SectionConfig[] = [
  {
    key: 'extra-pay-period-adjustment',
    title: 'Extra pay-period adjustment',
    categoryLabel: 'Extra pay-period adjustment',
    categories: [DATA_CATEGORY.ExtraPayPeriodAdjustment],
    fields: [
      { key: 'frequency', label: 'Frequency' },
      { key: 'payPeriods', label: 'Pay Periods' },
      { key: 'adjustmentFactor', label: 'Adjustment Factor', formatter: 'percent' },
    ],
    selectorSubtitle: 'ExtraPayPeriodAdjustment',
    emptyMessage: 'No extra pay-period adjustment rows returned for this selection.',
  },
  {
    key: 'frequency-conversion',
    title: 'Frequency conversion',
    categoryLabel: 'Frequency conversion',
    categories: [DATA_CATEGORY.FrequencyConversion],
    fields: [
      { key: 'frequency', label: 'Frequency' },
      { key: 'annualMultiplier', label: 'Annual Multiplier' },
      { key: 'daysPerPeriod', label: 'Days Per Period' },
    ],
    selectorSubtitle: 'FrequencyConversion',
    emptyMessage: 'No frequency conversion rows returned for this selection.',
  },
];

const SECTION_CONFIGS: Record<string, SectionConfig[]> = {
  adjustments: ADJUSTMENT_SECTIONS,
  'age-rules': AGE_RULE_SECTIONS,
  'medicare-stsl': MEDICARE_STSL_SECTIONS,
  offsets: OFFSET_SECTIONS,
  termination: TERMINATION_SECTIONS,
};

export const buildTaxConfigurationSections = (
  groupKey: string,
  groupDetails: TaxConfigurationGroupDetailResponse | null,
) => (SECTION_CONFIGS[groupKey] ?? []).map((config) => buildSection(config, groupDetails));
